/**
 * Shared product constants. SPEC.md §45, §46, §10.2, §6.2, §8.6.
 */

/** Soft maximum active players — the circular phone UI gets crowded past this. §45 */
export const MAX_PLAYERS = 12;

/** A round cannot start with fewer than this many active players. §34.1 */
export const MIN_PLAYERS_TO_START = 2;

/** Display names are capped so the ring stays readable. §8.6 */
export const PLAYER_NAME_MAX_LENGTH = 20;
export const PLAYER_NAME_MIN_LENGTH = 1;

/** Increment presets offered under the +/- controls, and the default. §10.2 */
export const INCREMENT_PRESETS = [1, 5, 10] as const;
export const DEFAULT_INCREMENT = 1;

/** V1 is integer points only. §10.3 */
export const MAX_TRANSFER_AMOUNT = 1_000_000;
export const MIN_TRANSFER_AMOUNT = 1;

/** Undo snackbar lifetime in ms. §12.1 */
export const UNDO_TOAST_DURATION_MS = 5_000;

/** Idempotency keys are opaque client-generated strings. §38 */
export const IDEMPOTENCY_KEY_MIN_LENGTH = 16;
export const IDEMPOTENCY_KEY_MAX_LENGTH = 128;

/**
 * Join-code alphabets. Ambiguous characters are excluded so a code can be
 * dictated across a noisy table: no I/1, no O/0. §6.2
 */
export const JOIN_CODE_LETTERS = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
export const JOIN_CODE_DIGITS = '23456789';
export const JOIN_CODE_ALPHABET = JOIN_CODE_LETTERS + JOIN_CODE_DIGITS;
export const DEFAULT_JOIN_CODE_LENGTH = 6;

/** How many transfers a snapshot carries for the history preview. §15.5 */
export const SNAPSHOT_RECENT_TRANSFER_LIMIT = 50;

/**
 * Bundled avatars: illustrated characters, one WebP per key. §46
 *
 * Keys are stable IDs, and the artwork is addressed *by* the key — the file is
 * `${AVATAR_IMAGE_BASE}/${key}.webp` and nothing else maps between the two. A
 * lookup table from key to filename would be a second place for the set to be
 * written down, and the half that drifts is always the one nobody rebuilds. §60
 *
 * `scripts/build-avatars.py` is what produces the files, from the source
 * artwork in `avatars/`: it derives these keys from the filenames, trims each
 * sticker to its own silhouette and scales it into the circle a seat draws it
 * in, so a wide character and a tall one carry the same weight on the ring.
 * Run it with `--check` to assert the set on disk still matches this list.
 *
 * **The order below is the artist's, and it is carried by the key.** Each
 * source file is numbered, the number is part of the key, and the digits are
 * zero-padded to a fixed width — so the list sorts into the intended order on
 * its own and there is no second list saying what that order is. It also makes
 * the key total rather than nearly-unique: the set has carried one character
 * under two names before, and a key made of the name alone collides for such a
 * pair. A few entries carry a variant letter after the number (`009a`, `020b`),
 * which is the artist's note that the character was drawn more than once. §46
 *
 * **The labels are not derived from the keys, and cannot be.** The source
 * artwork is named in upper case, so `HELLOKITTY` gives the generator no word
 * boundary to find and no rule can tell it from `DEADPOOL`. Every multi-word
 * label is therefore spelled out by hand in `LABEL_OVERRIDES` in the script, and
 * the script refuses to run if one of those entries no longer points at any
 * artwork — a label is what a screen reader announces, and "Hellokitty" is a
 * perfectly valid string that nothing else in the app would ever object to. §19
 */
export interface AvatarOption {
  key: string;
  label: string;
}

export const AVATARS: readonly AvatarOption[] = [
  { key: 'avatar_001_chiikawabirds', label: 'Chiikawa Birds' },
  { key: 'avatar_002_melanie', label: 'Melanie' },
  { key: 'avatar_003_miffy', label: 'Miffy' },
  { key: 'avatar_004_snuffy', label: 'Snuffy' },
  { key: 'avatar_005_cheesemouse', label: 'Cheese Mouse' },
  { key: 'avatar_006_mokoko', label: 'Mokoko' },
  { key: 'avatar_007_tycoco', label: 'Tycoco' },
  { key: 'avatar_008_whitebear', label: 'White Bear' },
  { key: 'avatar_009a_labubu', label: 'Labubu' },
  { key: 'avatar_010_zimomo', label: 'Zimomo' },
  { key: 'avatar_011_kiiroitori', label: 'Kiiroitori' },
  { key: 'avatar_012_baku', label: 'Baku' },
  { key: 'avatar_013_chiffon', label: 'Chiffon' },
  { key: 'avatar_014_cinnamoroll', label: 'Cinnamoroll' },
  { key: 'avatar_015_corocorokuririn', label: 'Corocorokuririn' },
  { key: 'avatar_016_hangyodon', label: 'Hangyodon' },
  { key: 'avatar_017_hellokitty', label: 'Hello Kitty' },
  { key: 'avatar_018_kuromi', label: 'Kuromi' },
  { key: 'avatar_019_mocha', label: 'Mocha' },
  { key: 'avatar_020b_mymelody', label: 'My Melody' },
  { key: 'avatar_021_pochacco', label: 'Pochacco' },
  { key: 'avatar_022_pompompurin', label: 'Pompompurin' },
  { key: 'avatar_023_bomb', label: 'Bomb' },
  { key: 'avatar_024_chuck', label: 'Chuck' },
  { key: 'avatar_025_red', label: 'Red' },
  { key: 'avatar_026_paimon', label: 'Paimon' },
  { key: 'avatar_027_raidenshogun', label: 'Raiden Shogun' },
  { key: 'avatar_028_zhongli', label: 'Zhongli' },
  { key: 'avatar_029_alex', label: 'Alex' },
  { key: 'avatar_030_creeper', label: 'Creeper' },
  { key: 'avatar_031_steve', label: 'Steve' },
  { key: 'avatar_032_charizard', label: 'Charizard' },
  { key: 'avatar_033_eevee', label: 'Eevee' },
  { key: 'avatar_034a_pikachu', label: 'Pikachu' },
  { key: 'avatar_035_luigi', label: 'Luigi' },
  { key: 'avatar_036_mario', label: 'Mario' },
  { key: 'avatar_037_princesspeach', label: 'Princess Peach' },
  { key: 'avatar_038_batman', label: 'Batman' },
  { key: 'avatar_039_superman', label: 'Superman' },
  { key: 'avatar_040_wonderwoman', label: 'Wonder Woman' },
  { key: 'avatar_041_captainamerica', label: 'Captain America' },
  { key: 'avatar_042_deadpool', label: 'Deadpool' },
  { key: 'avatar_043_doctorstrange', label: 'Doctor Strange' },
  { key: 'avatar_044_hulk', label: 'Hulk' },
  { key: 'avatar_045_ironman', label: 'Iron Man' },
  { key: 'avatar_046_nickfury', label: 'Nick Fury' },
  { key: 'avatar_047_spiderman', label: 'Spider-Man' },
  { key: 'avatar_048_thor', label: 'Thor' },
  { key: 'avatar_049_wolverine', label: 'Wolverine' },
  { key: 'avatar_050_eren', label: 'Eren' },
  { key: 'avatar_051_levi', label: 'Levi' },
  { key: 'avatar_052_mikasa', label: 'Mikasa' },
  { key: 'avatar_053_l', label: 'L' },
  { key: 'avatar_054_lightyagami', label: 'Light Yagami' },
  { key: 'avatar_055_ryuk', label: 'Ryuk' },
  { key: 'avatar_056_akaza', label: 'Akaza' },
  { key: 'avatar_057_nezuko', label: 'Nezuko' },
  { key: 'avatar_058_tanjiro', label: 'Tanjiro' },
  { key: 'avatar_059_zenitsu', label: 'Zenitsu' },
  { key: 'avatar_060_goku', label: 'Goku' },
  { key: 'avatar_061_piccolo', label: 'Piccolo' },
  { key: 'avatar_062_vegeta', label: 'Vegeta' },
  { key: 'avatar_063b_gojo', label: 'Gojo' },
  { key: 'avatar_064_megumi', label: 'Megumi' },
  { key: 'avatar_065_yuji', label: 'Yuji' },
  { key: 'avatar_066_bakugo', label: 'Bakugo' },
  { key: 'avatar_067_deku', label: 'Deku' },
  { key: 'avatar_068_todoroki', label: 'Todoroki' },
  { key: 'avatar_069_naruto', label: 'Naruto' },
  { key: 'avatar_070_sakura', label: 'Sakura' },
  { key: 'avatar_071_sasuke', label: 'Sasuke' },
  { key: 'avatar_072_luffy', label: 'Luffy' },
  { key: 'avatar_073_nami', label: 'Nami' },
  { key: 'avatar_074_zoro', label: 'Zoro' },
  { key: 'avatar_075_chahaein', label: 'Cha Hae-In' },
  { key: 'avatar_076_igris', label: 'Igris' },
  { key: 'avatar_077_sungjinwoo', label: 'Sung Jinwoo' },
  { key: 'avatar_078_anya', label: 'Anya' },
  { key: 'avatar_079_gyokuyou', label: 'Gyokuyou' },
  { key: 'avatar_080a_maomao', label: 'MaoMao' },
  { key: 'avatar_081_xiaolan', label: 'Xiaolan' },
  { key: 'avatar_082_mortysmith', label: 'Morty Smith' },
  { key: 'avatar_083_ricksanchez', label: 'Rick Sanchez' },
  { key: 'avatar_084_summersmith', label: 'Summer Smith' },
  { key: 'avatar_085_patrick', label: 'Patrick' },
  { key: 'avatar_086_spongebob', label: 'SpongeBob' },
  { key: 'avatar_087_squidward', label: 'Squidward' },
  { key: 'avatar_088_homersimpson', label: 'Homer Simpson' },
  { key: 'avatar_089_grizzly', label: 'Grizzly' },
  { key: 'avatar_090_icebear', label: 'Ice Bear' },
  { key: 'avatar_091_panda', label: 'Panda' },
  { key: 'avatar_092_bob', label: 'Bob' },
  { key: 'avatar_093_gru', label: 'Gru' },
  { key: 'avatar_094_kevin', label: 'Kevin' },
  { key: 'avatar_095_donaldduck', label: 'Donald Duck' },
  { key: 'avatar_096_mickeymouse', label: 'Mickey Mouse' },
  { key: 'avatar_097_minniemouse', label: 'Minnie Mouse' },
  { key: 'avatar_098_anna', label: 'Anna' },
  { key: 'avatar_099_elsa', label: 'Elsa' },
  { key: 'avatar_100_olaf', label: 'Olaf' },
  { key: 'avatar_101_harrypotter', label: 'Harry Potter' },
  { key: 'avatar_102_hermionegranger', label: 'Hermione Granger' },
  { key: 'avatar_103_ronweasley', label: 'Ron Weasley' },
  { key: 'avatar_104_darthvader', label: 'Darth Vader' },
  { key: 'avatar_105_r2_d2', label: 'R2-D2' },
  { key: 'avatar_106_yoda', label: 'Yoda' },
  { key: 'avatar_107_alien', label: 'Alien' },
  { key: 'avatar_108_buzzlightyear', label: 'Buzz Lightyear' },
  { key: 'avatar_109_woody', label: 'Woody' },
  { key: 'avatar_110_bumblebee', label: 'Bumblebee' },
  { key: 'avatar_111_megatron', label: 'Megatron' },
  { key: 'avatar_112_optimusprime', label: 'Optimus Prime' },
] as const;

export const AVATAR_KEYS: readonly string[] = AVATARS.map((a) => a.key);
/**
 * What an unpicked form starts on, and what an unrecognised key resolves to.
 * The first of the set, so the picker opens with its selection already on
 * screen rather than scrolled to somewhere in the middle. §6.3
 */
export const DEFAULT_AVATAR_KEY = 'avatar_001_chiikawabirds';

/**
 * Where the built artwork is served from. An absolute path, not a bundler
 * import: this module is compiled for the server too, and the set has to be
 * addressable from a plain key without a build step in the way.
 */
export const AVATAR_IMAGE_BASE = '/avatars';

/** Native pixel size of every generated file — what `<img width height>` gets. */
export const AVATAR_IMAGE_PIXELS = 224;

/**
 * The second size, drawn only by the winner celebration. §4.5
 *
 * That overlay is the one surface that shows an avatar across most of a
 * viewport, where the seat file — sized so a 76 px seat never upscales — would
 * be blown up nearly fourfold. Same keys, same directory layout, one extra path
 * segment, so a key that resolves to a seat avatar resolves to this too.
 */
export const AVATAR_CELEBRATION_PIXELS = 768;

const AVATAR_BY_KEY = new Map(AVATARS.map((a) => [a.key, a]));

/** Unknown keys fall back to the default avatar rather than rendering nothing. */
export function avatarFor(key: string): AvatarOption {
  return AVATAR_BY_KEY.get(key) ?? AVATAR_BY_KEY.get(DEFAULT_AVATAR_KEY)!;
}

/**
 * Artwork URL for a key. Resolves through `avatarFor`, so a key from a release
 * that no longer ships its picture asks for a file that exists instead of a 404
 * — the seat shows the fallback character rather than a broken-image glyph.
 */
export function avatarSrc(key: string): string {
  return `${AVATAR_IMAGE_BASE}/${avatarFor(key).key}.webp`;
}

/**
 * Artwork URL for the winner celebration — the large variant of the same key.
 *
 * Resolves through `avatarFor` for the same reason `avatarSrc` does, so the
 * overlay of a player whose stored key predates the current set shows the
 * fallback character rather than nothing at all. §46
 */
export function avatarCelebrationSrc(key: string): string {
  return `${AVATAR_IMAGE_BASE}/celebration/${avatarFor(key).key}.webp`;
}

export function isKnownAvatarKey(key: string): boolean {
  return AVATAR_BY_KEY.has(key);
}

/**
 * One character per player, and the set is how a table tells two Mikes apart. §8.6
 *
 * Duplicate *names* are allowed — two friends called Mike is a fact about the
 * room, not an error — so the avatar is the only thing left that reliably says
 * which seat is whose on a ring of twelve. Two players on the same face takes
 * that away, and the ring is the one surface with no room to say anything else.
 * The set is 112 and a table is at most twelve, so nobody is ever squeezed out
 * of a choice. §45, §46
 *
 * `ownKey` is the caller's current avatar, which is never "taken" from their own
 * point of view: a player editing their profile must not be told they cannot
 * keep the character they already have.
 */
export function isAvatarTaken(
  key: string,
  takenKeys: readonly string[],
  ownKey?: string | null,
): boolean {
  if (ownKey != null && key === ownKey) return false;
  return takenKeys.includes(key);
}

/**
 * The first character nobody at the table has, for a form to start on. §6.3
 *
 * A form that opened on a taken avatar would open on a selection it would then
 * refuse to submit, so the default is chosen against the table rather than
 * fixed. Falls back to the default key only if every avatar were somehow spoken
 * for, which a set of 112 and a cap of twelve makes unreachable.
 */
export function firstFreeAvatarKey(takenKeys: readonly string[]): string {
  const taken = new Set(takenKeys);
  return AVATARS.find((avatar) => !taken.has(avatar.key))?.key ?? DEFAULT_AVATAR_KEY;
}

/**
 * The winner celebration. §4.5
 *
 * The winner's own avatar dances on its podium: one short cycle a second —
 * a hop, a lean each way and a squash on the landing — thirty times over, which
 * is thirty seconds.
 *
 * The numbers live here rather than in the stylesheet because two things have to
 * agree on them: the CSS that moves the avatar and the timer that stops it. A
 * stylesheet saying `1s` and a store saying `30_000` are the same rule written
 * twice, and the half that drifts leaves either an avatar dancing after the
 * table has moved on or a celebration cut off mid-hop. The component sets
 * duration and iteration count from these; the stylesheet only says what one
 * cycle *looks* like. §60
 *
 * A second is deliberately in the middle of the 0.8–1.2 s band the effect is
 * specified in: fast enough to read as a dance, slow enough not to be a jitter
 * beside the numbers the table is trying to read. §19, §20.4
 */
export const CELEBRATION_DANCE_CYCLE_MS = 1_000;
export const WINNER_CELEBRATION_CYCLES = 30;
export const WINNER_CELEBRATION_DURATION_MS =
  WINNER_CELEBRATION_CYCLES * CELEBRATION_DANCE_CYCLE_MS;

/**
 * The drink cheer. §4.6
 *
 * The same dance on the same avatar, and deliberately only four cycles of it:
 * finishing a drink is a thing that happens several times an hour, and an effect
 * that ran as long as a won hand would be on screen more often than it is off.
 * It shares `CELEBRATION_DANCE_CYCLE_MS` because a cycle is a cycle — what tells
 * the two apart is how many, not how fast.
 */
export const DRINK_CELEBRATION_CYCLES = 4;
export const DRINK_CELEBRATION_DURATION_MS =
  DRINK_CELEBRATION_CYCLES * CELEBRATION_DANCE_CYCLE_MS;

/**
 * Drinking mode. §4.6
 *
 * A variant where the points deliberately do **not** balance. The winner of a
 * hand takes only the largest loss reported into it rather than the sum of them,
 * so every hand destroys points and the table drifts negative — and the way back
 * up is to finish a drink, which credits `pointsPerDrink` to whoever did.
 *
 * The number is chosen once, when the table is created, because it is the
 * exchange rate the whole night is priced in: changing it mid-game would make
 * two hands of the same size worth different amounts of drinking.
 */
export const MIN_POINTS_PER_DRINK = 1;
/**
 * A cup is a unit a table has to be able to *drink*, so the ceiling is much
 * lower than a transfer's. A five-figure cup is a typo, and the place to catch a
 * typo is where it is typed. §10.3
 */
export const MAX_POINTS_PER_DRINK = 1_000;
export const DEFAULT_POINTS_PER_DRINK = 10;

/** localStorage key holding a player's reconnect token for one session. §6.4 */
export function playerTokenStorageKey(sessionId: string): string {
  return `card-counter:player-token:${sessionId}`;
}

/** localStorage key for the per-session increment choice. §10.2 */
export function incrementStorageKey(sessionId: string): string {
  return `card-counter:increment:${sessionId}`;
}

/** Realtime room name for a session. §15.3 */
export function sessionRoom(sessionId: string): string {
  return `session:${sessionId}`;
}
